import axios from "axios";
import { ENVS, USER_API } from "config";
import { useCallback, useMemo } from "react";
import { authClient } from "lib/auth-client";
import { isJwtToken } from "lib/better-auth-session";
import {
  AuthSessionChangedError,
  captureAuthSession,
  isAuthSessionUnchanged,
  shareAuthSessionRefresh,
} from "lib/auth-session-ownership";
import { useAuth } from "store/hooks/useAuth";

interface UseTokenResponse {
  token: string;
  refreshToken: () => Promise<string>;
}

async function refreshBetterAuthSessionToken(): Promise<string> {
  const sessionResult = await authClient.getSession();
  const sessionToken = sessionResult.data?.session?.token;
  if (typeof sessionToken !== "string" || sessionToken.length === 0) {
    throw new Error("No Better Auth session");
  }
  return sessionToken;
}

export default function useAuthToken(): UseTokenResponse {
  const baseURL = ENVS.backendUrl;
  const { token, saveToken, saveRefreshToken } = useAuth();

  const axiosInstance = useMemo(() => {
    return axios.create({
      baseURL,
      timeout: 10000,
    });
  }, [baseURL]);

  const refreshToken = useCallback(async () => {
    // Read the store at call time: axios instances outlive the component that rendered this
    // hook, so the rendered refresh_token can be from before the latest sign-in.
    const owner = captureAuthSession();
    const currentRefreshToken = owner.refreshToken;
    if (!currentRefreshToken) {
      throw new Error("No refresh token available");
    }

    return shareAuthSessionRefresh(owner, async () => {
      if (!isAuthSessionUnchanged(owner)) throw new AuthSessionChangedError();
      try {
        let accessToken = currentRefreshToken;
        let nextRefreshToken = currentRefreshToken;

        if (!isJwtToken(currentRefreshToken)) {
          accessToken = await refreshBetterAuthSessionToken();
        } else {
          const response = await axiosInstance.post(USER_API.refreshToken, {
            refresh_token: currentRefreshToken,
          });

          const { access_token, refresh_token: newRefreshToken } = response.data.data;
          accessToken = access_token;
          nextRefreshToken = newRefreshToken;
        }

        if (!isAuthSessionUnchanged(owner)) throw new AuthSessionChangedError();
        saveRefreshToken(nextRefreshToken);
        saveToken(accessToken);
        return accessToken;
      } catch (error: unknown) {
        if (isAuthSessionUnchanged(owner)) {
          saveToken("");
          saveRefreshToken("");
        }
        throw error;
      }
    });
  }, [axiosInstance, saveRefreshToken, saveToken]);

  return { token, refreshToken };
}
